/**
 * Turning a recognised shape into something the board can hold, and into the
 * 150 ms morph that gets it there (SRS C5).
 *
 * The recogniser answers in measurements — a centre, two radii, a list of corners.
 * A board holds outlines. This is the one place that converts between them, so the
 * recogniser can be replaced without the pen engine learning a new vocabulary
 * (SRS C5.1), and the morph is here beside it because it is the same conversion run
 * part of the way.
 */

import type { ShapeMatch } from '@opal/contracts';
import type { Point, ShapeKind, ShapeType } from '@opal/shared';

import { easeOut } from './easing.js';
import { arrowOutline, CIRCLE_SEGMENTS } from './shape-geometry.js';

/** A recognised shape as the board stores it: an outline, and what it is. */
export interface CleanShape {
  /** Which of the six drawing kinds holds it (docs/05-DATA-MODEL.md §3.3). */
  readonly kind: ShapeKind;
  /** What it actually is, which is finer than the kind: a square, not a rectangle. */
  readonly shapeType: ShapeType;
  readonly points: readonly Point[];
  /** Only for a pentagon or a hexagon. */
  readonly sides?: number;
}

/**
 * Which drawing kind each recognised type is stored as.
 *
 * The data model keeps both: the kind is how the shape behaves, the type is what it
 * is. A snapped hexagon is a polygon with six sides, and a lecture that says so is
 * worth more later than one that only says "polygon".
 */
const KIND_FOR: Readonly<Record<ShapeType, ShapeKind>> = {
  line: 'line',
  arrow: 'arrow',
  rectangle: 'rectangle',
  square: 'rectangle',
  circle: 'circle',
  ellipse: 'circle',
  triangle: 'triangle',
  rightTriangle: 'triangle',
  pentagon: 'polygon',
  hexagon: 'polygon',
  polygon: 'polygon',
};

const SIDES_FOR: Readonly<Partial<Record<ShapeType, number>>> = {
  pentagon: 5,
  hexagon: 6,
};

/** A corner list drawn as a closed outline: back round to where it started. */
function closedOutline(corners: readonly Point[]): Point[] {
  const first = corners[0];
  if (!first) return [];
  return [...corners.map((point) => ({ x: point.x, y: point.y })), { x: first.x, y: first.y }];
}

/** The oval a recognised circle or ellipse describes, tilt included. */
function ovalOutline(match: ShapeMatch): Point[] {
  const centre = match.geometry.centre;
  const radiusX = match.geometry.radiusX ?? 0;
  const radiusY = match.geometry.radiusY ?? 0;
  if (!centre || radiusX <= 0 || radiusY <= 0) return [];

  const rotation = match.geometry.rotation ?? 0;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const points: Point[] = [];
  for (let step = 0; step <= CIRCLE_SEGMENTS; step += 1) {
    const angle = (step / CIRCLE_SEGMENTS) * Math.PI * 2;
    const x = radiusX * Math.cos(angle);
    const y = radiusY * Math.sin(angle);
    points.push({ x: centre.x + x * cos - y * sin, y: centre.y + x * sin + y * cos });
  }
  return points;
}

/** The outline of a recognised shape, or null when there is not enough of one. */
export function cleanShapeOf(match: ShapeMatch): CleanShape | null {
  const corners = match.geometry.points;
  const kind = KIND_FOR[match.shapeType];
  const sides = SIDES_FOR[match.shapeType];

  const points = pointsFor(match, kind, corners);
  if (points.length < 2) return null;

  return { kind, shapeType: match.shapeType, points, ...(sides ? { sides } : {}) };
}

function pointsFor(match: ShapeMatch, kind: ShapeKind, corners: readonly Point[]): Point[] {
  if (kind === 'circle') return ovalOutline(match);

  const from = corners[0];
  const to = corners[1];
  if (!from || !to) return [];
  if (kind === 'line') return [{ ...from }, { ...to }];
  if (kind === 'arrow') return arrowOutline(from, to);
  return closedOutline(corners);
}

/** One point along a path, by how far through it is rather than by index. */
function alongPath(path: readonly Point[], share: number): Point {
  const last = path.length - 1;
  const at = Math.max(0, Math.min(last, share * last));
  const index = Math.floor(at);
  const start = path[index] ?? path[last] ?? { x: 0, y: 0 };
  const end = path[index + 1] ?? start;
  const within = at - index;
  return { x: start.x + (end.x - start.x) * within, y: start.y + (end.y - start.y) * within };
}

/**
 * The shape part of the way from the ink to the clean form.
 *
 * The two paths are matched by how far through each of them a point is, not by
 * counting samples: a stroke has hundreds and a triangle has four, and pairing them
 * off one for one would leave most of the triangle standing still while the ink
 * collapsed onto one corner of it.
 *
 * `progress` is the plain share of the way through. The easing is applied here, so
 * every caller morphs on the design system's curve without having to remember to.
 */
export function morphBetween(
  from: readonly Point[],
  to: readonly Point[],
  progress: number,
): Point[] {
  const eased = easeOut(progress);
  if (to.length === 0) return from.map((point) => ({ x: point.x, y: point.y }));

  return to.map((target, index) => {
    const share = to.length === 1 ? 0 : index / (to.length - 1);
    const start = alongPath(from, share);
    return {
      x: start.x + (target.x - start.x) * eased,
      y: start.y + (target.y - start.y) * eased,
    };
  });
}
